import { useEffect, useRef } from 'react';
import { useStore } from '@tasca/client-sdk';
import type { Channel, VoiceClient } from '@tasca/client-sdk';
import { hueOf, initials } from '../lib/avatar.js';
import { Icon } from '../ui/Icon.js';
import { VideoGrid } from './VideoGrid.js';

/**
 * O palco do canal de voz, por cima da conversa dele.
 *
 * Um canal de voz é um sítio, não um botão: tem gente, tem câmaras, tem
 * ecrãs partilhados e tem conversa escrita — para o link, para o "já venho",
 * para quem está sem microfone. Isto mostra as três primeiras; a quarta é a
 * conversa normal do canal, que continua por baixo porque um canal de voz é
 * um canal como os outros e as mensagens dele já funcionavam.
 *
 * O painel da coluna da esquerda continua a ser o comando — ele é o que se vê
 * mesmo depois de navegar para outro canal. Aqui não repetimos os botões:
 * repeti-los era dar dois sítios para desligar o microfone e nenhum deles ser
 * o sítio.
 */

/** Uma faixa que estamos a ENVIAR, para nos vermos a nós próprios. */
function SelfTile({
  track,
  label,
  kind,
}: {
  track: MediaStreamTrack;
  label: string;
  kind: 'cam' | 'screen';
}): React.ReactElement {
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    el.srcObject = new MediaStream([track]);
    return () => {
      el.srcObject = null;
    };
  }, [track]);

  return (
    <figure className={`stage-self ${kind}`}>
      {/* `muted` não é opcional: sem ele, o próprio áudio volta pelos
          altifalantes e faz eco de si mesmo. */}
      <video ref={video} autoPlay muted playsInline />
      <figcaption>{label}</figcaption>
    </figure>
  );
}

export function VoiceStage({
  channel,
  voice,
  connected,
  onJoin,
  onLeave,
  onUserMenu,
}: {
  channel: Channel;
  voice: VoiceClient;
  connected: boolean;
  onJoin: () => void;
  onLeave: () => void;
  /** Botão direito numa pessoa do palco: silenciar, desligar, expulsar. */
  onUserMenu?: (event: React.MouseEvent, userId: string, name: string) => void;
}): React.ReactElement {
  const { me, members, voiceStates, speaking, remoteStreams, voiceState } = useStore();

  const here = [...voiceStates.values()].filter((v) => v.channelId === channel.id);
  const memberList = channel.guildId ? (members.get(channel.guildId) ?? []) : [];
  const nameOf = (userId: string): string => {
    if (userId === me?.id) return me.displayName;
    const member = memberList.find((m) => m.userId === userId);
    return member ? (member.nick ?? member.user.displayName) : 'Utilizador';
  };

  // Vídeo dos outros só existe se estivermos ligados a este canal: o media
  // vem pelo SFU, e para isso é preciso lá estar.
  const videoStreams = connected ? remoteStreams.filter((s) => s.kind === 'video') : [];
  const selfTracks = connected
    ? [
        voice.cameraTrack
          ? { track: voice.cameraTrack, label: 'A tua câmara', kind: 'cam' as const }
          : null,
        voice.screenTrack
          ? { track: voice.screenTrack, label: 'O teu ecrã', kind: 'screen' as const }
          : null,
      ].filter((t) => t !== null)
    : [];

  const people = here
    .map((state) => ({ state, name: nameOf(state.userId) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-PT'));

  return (
    <section className="voice-stage" aria-label={`Voz em ${channel.name ?? 'canal'}`}>
      <header className="stage-head">
        <span className="sigil">
          <Icon name="volume" />
        </span>
        <strong>
          {people.length === 0
            ? 'Ninguém em voz'
            : people.length === 1
              ? '1 pessoa em voz'
              : `${people.length} pessoas em voz`}
        </strong>
        <div className="spacer" />
        {connected ? (
          <button className="stage-action leave" onClick={onLeave}>
            <Icon name="phoneOff" />
            Sair da voz
          </button>
        ) : (
          <button className="stage-action" onClick={onJoin}>
            <Icon name="volume" />
            Entrar em voz
          </button>
        )}
      </header>

      {(videoStreams.length > 0 || selfTracks.length > 0) && (
        <div className="stage-video">
          <VideoGrid streams={videoStreams} voice={voice} />
          {selfTracks.length > 0 && (
            <div className="stage-selves">
              {selfTracks.map((t) => (
                <SelfTile key={t.track.id} track={t.track} label={t.label} kind={t.kind} />
              ))}
            </div>
          )}
        </div>
      )}

      {people.length === 0 ? (
        <p className="hint">
          Ainda não está cá ninguém. Entra, e quem vier a seguir vê-te aqui.
        </p>
      ) : (
        <ul className="stage-people">
          {people.map(({ state, name }) => {
            const isMe = state.userId === me?.id;
            const muted = state.selfMute || state.serverMute;
            // A própria fala vem do VAD local, que não espera pelo servidor.
            const isSpeaking = speaking.has(isMe ? 'self' : state.userId) && !muted;

            return (
              <li
                key={state.userId}
                className={isSpeaking ? 'speaking' : ''}
                onContextMenu={(e) => onUserMenu?.(e, state.userId, name)}
              >
                <span className="avatar" style={{ ['--hue' as string]: hueOf(state.userId) }}>
                  {initials(name)}
                </span>
                <span className="stage-name">{name}</span>
                <span className="stage-flags">
                  {state.serverMute && (
                    <span className="voice-flag mod" title="silenciado por moderação">
                      <Icon name="micOff" />
                    </span>
                  )}
                  {state.selfMute && !state.serverMute && (
                    <span className="voice-flag" title="sem microfone">
                      <Icon name="micOff" />
                    </span>
                  )}
                  {(state.selfDeaf || state.serverDeaf) && (
                    <span className="voice-flag" title="sem áudio">
                      <Icon name="headphonesOff" />
                    </span>
                  )}
                  {state.selfStream && (
                    <span className="voice-flag live" title="a partilhar ecrã">
                      <Icon name="screenShare" />
                    </span>
                  )}
                  {state.selfVideo && (
                    <span className="voice-flag live" title="com câmara">
                      <Icon name="video" />
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Estamos cá, mas o media ainda não ligou. Dizê-lo evita a leitura de
          que a voz está partida enquanto ainda está a negociar. */}
      {connected && voiceState !== 'connected' && (
        <p className="hint">
          {voiceState === 'failed' ? 'A ligação de voz falhou.' : 'A ligar o áudio…'}
        </p>
      )}
    </section>
  );
}
